import apiInstance from '@/api';
import { BackgroundTask } from '@/pages/tools/RevaluationTool/types';
import { ingestStreamedTasks } from '@/store/backgroundTasks/backgroundTasksThunks';
import { TASK_POLL_LIMIT } from '@/store/backgroundTasks/constants';
import { useAppDispatch } from '@store/hooks';
import { useEffect } from 'react';

/**
 * Keeps the notification bell in sync with a single Server-Sent Events stream
 * instead of polling `GET /v2/admin/tasks/` on a timer.
 *
 * The stream pushes the full task list on connect and again on every change
 * (new run, status change, ~2s progress tick, completion, cancellation); each
 * message is the same array `listTasks` returns, so we hand it straight to
 * `ingestStreamedTasks`, which diffs for completions and updates the store.
 *
 * Auth is session-cookie based and the endpoint requires no custom headers, so
 * a native `EventSource` is the right client: unlike a `fetch` stream carrying
 * the app's `Viper-Version` header, it issues no CORS preflight — which is what
 * lets it connect in the cross-origin dev setup (frontend :5173 → API :8008).
 * `EventSource` also reconnects on its own when the server closes the
 * connection (~30 min cap) or on a transient drop, re-sending the snapshot to
 * re-sync — so there is deliberately no manual retry or polling fallback here.
 *
 * `reconnectKey` forces a fresh connection whenever it changes — the panel
 * bumps it on open so opening the bell always (re)establishes the stream and
 * pulls a current snapshot, even if the persistent connection had dropped.
 */
export function useBackgroundTasksStream(enabled: boolean, reconnectKey = 0) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!enabled) return undefined;

    const url = apiInstance.revalueTrades.getTasksStreamUrl({
      active: false,
      limit: TASK_POLL_LIMIT,
    });
    // withCredentials sends the session cookie on the cross-origin stream.
    const source = new EventSource(url, { withCredentials: true });

    source.onmessage = (event) => {
      if (!event.data) return;
      let tasks: BackgroundTask[];
      try {
        tasks = JSON.parse(event.data);
      } catch {
        return;
      }
      if (Array.isArray(tasks)) dispatch(ingestStreamedTasks(tasks));
    };

    // EventSource auto-reconnects on transient errors, so there's nothing to do
    // here. A genuinely dead endpoint just retries in the background; the app's
    // auth layer handles a redirect to login on the next regular API call.
    source.onerror = () => {};

    return () => source.close();
  }, [enabled, reconnectKey, dispatch]);
}

export default useBackgroundTasksStream;
